import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress, 
  Grid, 
  Typography,
  Chip,
  Divider
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import rfidService, { ReaderType } from '../../services/rfidService';
import toast from '../../utils/toast';

const ReaderTypeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [readerType, setReaderType] = useState<ReaderType | null>(null);

  useEffect(() => {
    if (id) {
      fetchReaderType(id);
    }
  }, [id]);

  const fetchReaderType = async (typeId: string) => {
    try {
      setLoading(true);
      const data = await rfidService.getReaderTypeById(typeId);
      setReaderType(data);
    } catch (error) {
      console.error('Error fetching reader type:', error);
      toast.error('Failed to fetch reader type details');
      navigate('/rfid/reader-types');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!readerType) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          Reader type not found
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          component={Link} 
          to="/rfid/reader-types"
          sx={{ mt: 2 }}
        >
          Back to Reader Types
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Reader Type Details
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          component={Link}
          to={`/rfid/reader-types/${id}/edit`}
        >
          Edit Reader Type
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Name
              </Typography>
              <Typography variant="body1">{readerType.name}</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Code
              </Typography>
              <Typography variant="body1">{readerType.code}</Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textSecondary">
                Description
              </Typography>
              <Typography variant="body1">
                {readerType.description || 'No description provided'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Status
              </Typography>
              <Chip 
                label={readerType.is_active ? 'Active' : 'Inactive'} 
                color={readerType.is_active ? 'success' : 'error'} 
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Created At
              </Typography>
              <Typography variant="body1">
                {new Date(readerType.created_at).toLocaleString()}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Updated At
              </Typography>
              <Typography variant="body1">
                {new Date(readerType.updated_at).toLocaleString()}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Created By
              </Typography>
              <Typography variant="body1">
                {readerType.created_by || 'Unknown'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Updated By
              </Typography>
              <Typography variant="body1">
                {readerType.updated_by || 'Unknown'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Box mt={3}>
        <Button 
          variant="outlined" 
          color="primary" 
          component={Link} 
          to="/rfid/reader-types"
        >
          Back to Reader Types
        </Button>
      </Box>
    </Box>
  );
};

export default ReaderTypeDetailPage;